import type { ChampsNotation, CleEliminatoire, MajContact, Reponses, Scores, TypeContact } from '@rh/shared';

/**
 * Saisies du panneau de contact gardées sur le téléphone (docs/UI.md § Écran 3,
 * mode hors ligne). Elles ne portent que la saisie et des identifiants : jamais
 * de nom, de téléphone, de fiche ni de jeton. La **structure** de la base
 * évolue par `VERSION_BASE`, la **forme** d'un élément par `format`
 * (`format.ts`).
 */

export const FORMAT_SAISIES = 1 as const;
export const NOM_BASE = 'rh-app-saisies';
export const VERSION_BASE = 1;

/** Clé composée : deux comptes sur le même contact ne s'écrasent jamais. */
export type CleSaisie = readonly [utilisateurId: string, contactId: string];

/** Ce que le téléphone sait du contact ouvert : de quoi le reconnaître, rien de plus. */
export interface RefContact {
  id: string;
  candidatureId: string;
  type: TypeContact;
  debuteLe: string;
}

export interface BaremeAffiche {
  id: string;
  version: number;
}

/** Formulaire d'appel tel que saisi (valeurs brutes des champs, `datetime-local` compris). */
export interface SaisieAppelLocale {
  type: 'telephonique';
  joignable: boolean | null;
  disponible: boolean | null;
  interesse: boolean | null;
  potentiel: number | null;
  rdv: string;
  lieu: string;
  rappel: string;
  observations: string;
}

/** Formulaire d'entretien tel que saisi : trame, notation en cours, barème affiché. */
export interface SaisieEntretienLocale {
  type: 'presentiel';
  reponses: Reponses;
  observations: string;
  scores: Scores;
  eliminatoire: boolean;
  motif: CleEliminatoire | '';
  precision: string;
  forcer: boolean;
  noteForcee: string;
  justification: string;
  bareme: BaremeAffiche | null;
}

export type SaisieLocale = SaisieAppelLocale | SaisieEntretienLocale;

/** PUT complet à envoyer ; la révision distingue une fusion arrivée pendant l'envoi. */
export interface EtapeEnregistrement {
  corps: MajContact;
  revision: number;
}

/** Dernière étape : clôture d'appel ou évaluation, identifiée par son `envoiId`. */
export type FinContact =
  | { genre: 'cloture'; envoiId: string; termineLe: string }
  | {
      genre: 'evaluation';
      envoiId: string;
      evalueeLe: string;
      entree: ChampsNotation;
      bareme: BaremeAffiche;
      noteAffichee: number;
    };

export interface RefusEnvoi {
  etape: 'enregistrement' | 'fin';
  statut: number;
  code: string | null;
  message: string;
  details: unknown;
  bareme: {
    affiche: { id: string; version: number | null };
    applicable: { id: string; version: number };
  } | null;
  le: number;
  /** Fiche anonymisée ou supprimée : la saisie a quitté le téléphone, seule la trace reste. */
  contenuEfface: boolean;
  action: 'envoyer_evaluation_seule' | null;
}

export interface EnvoiContact {
  format: typeof FORMAT_SAISIES;
  utilisateurId: string;
  contactId: string;
  candidatureId: string;
  type: TypeContact;
  debuteLe: string;
  /** Rang d'envoi, unique sur le téléphone : les saisies partent dans l'ordre où elles ont été faites. */
  ordre: number;
  /** Première écriture locale (brouillon compris). */
  creeLe: number;
  /** `creeLe` + 7 jours, jamais prolongé. */
  expireLe: number;
  modifieLe: number;
  derniereRevision: number;
  enregistrement: EtapeEnregistrement | null;
  fin: FinContact | null;
  saisie: SaisieLocale | null;
  etat: 'en_attente' | 'refuse';
  refus: RefusEnvoi | null;
  tentatives: number;
  prochaineTentativeLe: number;
  dernierePanne: { le: number; message: string } | null;
}

export interface Brouillon {
  format: typeof FORMAT_SAISIES;
  utilisateurId: string;
  contactId: string;
  candidatureId: string;
  type: TypeContact;
  debuteLe: string;
  saisie: SaisieLocale;
  revision: number;
  /** Révision du brouillon reprise par la dernière mise en file ; null si rien n'est parti de lui. */
  revisionMiseEnFile: number | null;
  onglet: string;
  creeLe: number;
  modifieLe: number;
  expireLe: number;
}

interface BaseDemande {
  utilisateurId: string;
  contact: RefContact;
  saisie: SaisieLocale;
  brouillonRevision: number | null;
}

export type DemandeMiseEnFile =
  | (BaseDemande & { genre: 'enregistrement'; corps: MajContact })
  | (BaseDemande & { genre: 'cloture'; corps: MajContact; termineLe: string })
  | (BaseDemande & {
      genre: 'evaluation';
      corps: MajContact | null;
      entree: ChampsNotation;
      bareme: BaremeAffiche;
      noteAffichee: number;
      evalueeLe: string;
    });

export type ResultatMiseEnFile =
  | { ok: true; envoi: EnvoiContact }
  | { ok: false; raison: 'FIN_DEJA_EN_FILE' | 'STOCKAGE_INDISPONIBLE'; message: string };

/** Résumé sans contenu libre, pour les listes, l'historique et les bandeaux. */
export interface ResumeEnvoi {
  utilisateurId: string;
  contactId: string;
  candidatureId: string;
  type: TypeContact;
  debuteLe: string;
  etat: 'en_attente' | 'refuse' | 'brouillon';
  etapes: { enregistrement: boolean; fin: 'cloture' | 'evaluation' | null };
  modifieLe: number;
  heureFin: string | null;
  potentiel: number | null;
  noteAffichee: number | null;
  baremeVersion: number | null;
  refus: Pick<RefusEnvoi, 'code' | 'message' | 'contenuEfface' | 'action'> | null;
  tentatives: number;
  prochaineTentativeLe: number;
  dernierePanne: string | null;
  expireLe: number;
}

/** Nombres seulement : l'écran de connexion et les dialogues n'affichent jamais de contenu. */
export interface Recensement {
  total: number;
  parUtilisateur: Record<string, number>;
  illisibles: number;
  plusProcheExpiration: number | null;
  expirentSous24h: number;
}
